import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {isEnterKey, isSpaceKey} from 'util/Keyboard';
import {ElementKeyboardEvent} from 'util/Types';

export interface SectionTabsProps {
  className?: string;
  tabs: Array<SectionTab>;
}

export interface SectionTab {
  /** Callback that is triggered if the SectionTab is not active */
  handleClick: () => void;
  /** Callback that is triggered if the SectionTab is active */
  handleActiveClick?: () => void;
  /** Whether this tab is currently selected */
  isActive: boolean;
  /** Text to display on the tab */
  text: string;
  /**
   * A unique-within-the-DOM ID for the tab. This should also be set as the `aria-labeledby` on the associated
   * tabpanel.
   */
  id: string;
  /** The unique-within-the-DOM ID for the panel controlled by this tab. */
  ariaControls: string;
  dataTestAttrs?: DataTestAttrs;
}

function InnerTab({
  ariaControls,
  dataTestAttrs,
  handleActiveClick,
  handleClick,
  id,
  isActive,
  text,
}: SectionTab): JSX.Element {
  const clickCallback = React.useCallback(() => {
    if (isActive && handleActiveClick) {
      handleActiveClick();
    } else if (!isActive) {
      handleClick();
    }
  }, [isActive, handleClick, handleActiveClick]);

  return (
    <li
      className={classNames('sectionTabs-navItem', {active: isActive})}
      onClick={clickCallback}
      onKeyDown={(e: ElementKeyboardEvent) => {
        if (isSpaceKey(e) || isEnterKey(e)) {
          clickCallback();
        }
      }}
      id={id}
      role="tab"
      aria-controls={ariaControls}
      aria-selected={isActive}
      tabIndex={isActive && !handleActiveClick ? -1 : 0}
      {...getTestProps({component: 'SectionTab', element: 'navItem', ...(dataTestAttrs ?? {})})}
    >
      <div className="sectionTabs-navItem-title">
        <span className="sectionTabs-navItem-title-inner">{text}</span>
      </div>
    </li>
  );
}

export default function SectionTabs(props: SectionTabsProps): JSX.Element {
  return (
    <div
      className={classNames('sectionTabs', props.className)}
      {...getTestProps({
        component: 'SectionTabs',
        element: 'root',
      })}
    >
      <ul className="sectionTabs-nav" role="tablist">
        {props.tabs.map((tab, i) => (
          <InnerTab key={i} {...tab} />
        ))}
      </ul>
    </div>
  );
}

SectionTabs.propTypes = {
  className: PropTypes.string,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      handleClick: PropTypes.func.isRequired,
      handleActiveClick: PropTypes.func,
      isActive: PropTypes.bool.isRequired,
      text: PropTypes.string.isRequired,
    }).isRequired
  ).isRequired,
};
